import { type FC, type ComponentProps, useRef, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { breakpoints } from 'dibs-sassy/exports/breakpoints';
import { spacing } from 'dibs-sassy/exports/spacing';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { trackEcommerce, eventNameConstants } from 'dibs-tracking';

import { useHomePageContext } from '../HomePageContext';

import { type HpSharedShopByModuleItemVariant_trendingCreator$key } from './__generated__/HpSharedShopByModuleItemVariant_trendingCreator.graphql';

import dibsCss from 'dibs-css';
import styles from './HpSharedShopByModuleItemVariant.module.css';

const trendingCreatorFragment = graphql`
    fragment HpSharedShopByModuleItemVariant_trendingCreator on TrendingCreator {
        category
        trendingVerticalLinkData {
            path
            ...SeoLink_linkData
        }
        creator {
            displayName
        }
        firstItemPhotoWebPath(size: master)
    }
`;

const RESP_IMG = 135;
const RESP_IMG_SCALED = Math.ceil(RESP_IMG * 1.1);
const RESP_CIRCLE = 250;
const MOBILE_IMG = 54;
const MOBILE_CIRCLE = 99;

const categoryCopies = {
    A: <FormattedMessage id="HpSharedShopByModuleVariant.art" defaultMessage="Art" />,
    V: <FormattedMessage id="HpSharedShopByModuleVariant.fashion" defaultMessage="Fashion" />,
    J: <FormattedMessage id="HpSharedShopByModuleVariant.jewelry" defaultMessage="Jewelry" />,
    F: <FormattedMessage id="HpSharedShopByModuleVariant.furniture" defaultMessage="Furniture" />,
};

export const HpSharedShopByModuleItemVariant: FC<{
    trendingCreator?: HpSharedShopByModuleItemVariant_trendingCreator$key | null;
    position: number;
}> = ({ trendingCreator: trendingCreatorRef, position }) => {
    const trendingCreator = useFragment(trendingCreatorFragment, trendingCreatorRef);
    const { isMobile } = useHomePageContext();
    const elementRef = useRef<HTMLDivElement>(null);

    const {
        firstItemPhotoWebPath: imgSrc,
        creator,
        category,
        trendingVerticalLinkData: linkData,
    } = trendingCreator || {};
    // Remove soft hyphen from displayName
    const displayName = creator?.displayName?.replace(/\u00AD/g, '');
    const categoryCopy = categoryCopies[category as keyof typeof categoryCopies];

    const trendingVerticalPath = trendingCreator?.trendingVerticalLinkData?.path || '';

    const onClick = useCallback(() => {
        trackEcommerce({
            type: 'promoClick',
            eventName: eventNameConstants.EVENT_SELECT_PROMOTION,
            promotions: [
                {
                    creative: trendingVerticalPath,
                    id: 'trending creators',
                    name: 'trending creators',
                    position,
                },
            ],
        });
    }, [trendingVerticalPath, position]);

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(
            ({ isVisible }) => {
                if (isVisible) {
                    trackEcommerce({
                        type: 'promotionImpression',
                        eventName: eventNameConstants.EVENT_VIEW_PROMOTION,
                        promotions: [
                            {
                                creative: trendingVerticalPath,
                                id: 'trending creators',
                                name: 'trending creators',
                                position,
                            },
                        ],
                    });
                }
            },
            [trendingVerticalPath, position]
        );

    if (!imgSrc || !linkData || !displayName || !categoryCopy || !category) {
        return null;
    }

    // imgDisplaySize is the size of the image that is displayed on the screen
    // imgScaledSize is the size of the image that is scaled on hover
    const imgDisplaySize = isMobile ? MOBILE_IMG : RESP_IMG;
    const imgScaledSize = isMobile ? MOBILE_IMG : RESP_IMG_SCALED;
    const circleSize = isMobile ? MOBILE_CIRCLE : RESP_CIRCLE;

    return (
        <SeoLink
            linkData={linkData}
            className={classnames(
                styles.link,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.justifyCenter,
                dibsCss.itemsCenter
            )}
            style={{
                // Need to deduct the gap between the items for mobile so it would be visible
                flexBasis: isMobile ? `calc(50% - ${spacing.sassySpacingXSmall})` : RESP_CIRCLE,
            }}
            onClick={onClick}
        >
            <VisibilityTracker elementRef={elementRef} onVisibilityChange={onVisibilityChange} />
            <div
                ref={elementRef}
                className={classnames(
                    dibsCss.aspectSquare,
                    dibsCss.overflowHidden,
                    dibsCss.roundedFull,
                    dibsCss.bgWhite,
                    dibsCss.flex,
                    dibsCss.justifyCenter,
                    dibsCss.itemsCenter,
                    dibsCss.hFull,
                    dibsCss.wFull,
                    {
                        [dibsCss.pSmall]: isMobile,
                        [dibsCss.pLarge]: !isMobile,
                    }
                )}
                style={{
                    maxWidth: `${circleSize}px`,
                    maxHeight: `${circleSize}px`,
                }}
            >
                <img
                    src={`${imgSrc}?height=${imgScaledSize}`}
                    alt={displayName || ''}
                    className={classnames(dibsCss.hFull, dibsCss.wFull, dibsCss.objectContain, {
                        [styles.img]: !isMobile,
                    })}
                    style={{ maxWidth: `${imgDisplaySize}px`, maxHeight: `${imgDisplaySize}px` }}
                    srcSet={getSrcsetString(imgSrc, [
                        RESP_IMG_SCALED,
                        RESP_IMG_SCALED * 2,
                        MOBILE_IMG,
                        MOBILE_IMG * 2,
                    ])}
                    sizes={`(max-width: ${breakpoints.mobile.max}px) ${MOBILE_IMG}px, ${RESP_IMG}px`}
                />
            </div>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.textCenter,
                    dibsCss.mtSmall,
                    {
                        [dibsCss.gapXxsmall]: !isMobile,
                    }
                )}
            >
                <div className={dibsCss.sassyFontBody}>{categoryCopy}</div>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.justifyCenter,
                        dibsCss.itemsCenter,
                        dibsCss.gapXxsmall,
                        {
                            [dibsCss.sassyFontHeaderSmall]: isMobile,
                            [dibsCss.sassyFontHeaderMedium]: !isMobile,
                        }
                    )}
                >
                    {displayName}
                    {!isMobile && (
                        <ArrowRight className={classnames(dibsCss.wSmaller, dibsCss.hSmaller)} />
                    )}
                </div>
            </div>
        </SeoLink>
    );
};
